import { useCallback, useEffect, useState } from "react";
import { initTelegram, tg, tgUserName } from "@/telegram/tg";
import { Welcome } from "@/screens/onboarding/Welcome";
import { PhoneWeb } from "@/screens/onboarding/PhoneWeb";
import { WaitCall } from "@/screens/onboarding/WaitCall";
import { Email } from "@/screens/onboarding/Email";
import { Address } from "@/screens/onboarding/Address";
import { Kyc } from "@/screens/onboarding/kyc/Kyc";
import { Wallet } from "@/screens/purchase/Wallet";
import { Order } from "@/screens/purchase/Order";
import { Withdraw } from "@/screens/withdraw/Withdraw";
import { WithdrawStatus } from "@/screens/withdraw/WithdrawStatus";
import { UsdtWithdraw } from "@/screens/withdraw/UsdtWithdraw";
import { UsdtWithdrawStatus } from "@/screens/withdraw/UsdtWithdrawStatus";
import { Cards } from "@/screens/cards/Cards";
import { Card as CardScreen } from "@/screens/cards/Card";
import { CardTopUp, CardRefund } from "@/screens/cards/CardExchange";
import { CardCrypto } from "@/screens/cards/CardCrypto";
import { CardWithdraw, type CardWithdrawTarget } from "@/screens/cards/CardWithdraw";
import { TopUpUsdt } from "@/screens/topup/TopUpUsdt";
import { VerifyHub } from "@/screens/verify/VerifyHub";
import { Sof } from "@/screens/verify/Sof";
import { Status } from "@/screens/purchase/Status";
import { Support } from "@/screens/Support";
import { Agreement } from "@/screens/documents/Agreement";
import { Sherp } from "@/screens/documents/Sherp";
import { Profile, type KycState } from "@/screens/Profile";
import { ConnectionError } from "@/components/ConnectionError";
import { Spinner } from "@/components/Spinner";
import { authApi, accountApi, cardsApi, kycApi, ordersApi, partnerApi, sherpApi, withdrawalsApi, type Account, type ApiOrder, type ApiWithdrawal, type CardConfig, type CustodialWithdrawal, type IssuedCard, type KycStatus, type UiFlags, type UsdtAsset, type WaitCallRequest, type WithdrawalMethod } from "@/lib/api";
import {
  clearAll,
  getProfile,
  saveProfile,
  takeRefCode,
  updateProfile,
  type Profile as ProfileT,
} from "@/lib/storage";

type Step =
  | "welcome"
  | "phone"
  | "waitcall"
  | "email"
  | "address"
  | "kyc"
  | "agreement"
  | "sherp"
  | "profile"
  | "wallet"
  | "order"
  | "status"
  | "withdraw"
  | "withdraw_status"
  | "usdt_withdraw"
  | "usdt_withdraw_status"
  | "cards"
  | "card"
  | "card_topup"
  | "card_refund"
  | "card_crypto"
  | "card_withdraw"
  | "card_withdraw_form"
  | "topup_usdt"
  | "verify"
  | "sof"
  | "support";

type Draft = {
  phone?: string;
  wallet?: string;
  order?: ApiOrder;
};

// Единый поток для Telegram Mini App и веб-приложения. Различие только в среде:
// resetLabel — в вебе «Выйти», в Telegram скрыт (выход бессмыслен — initData).
// initialScreen — диплинк (напр. из кнопки «Поддержка»): открыть сразу нужный экран.
type Props = { resetLabel?: string; initialScreen?: "support" };

function kycStateFrom(k: KycStatus | null): KycState {
  if (k?.answer === "GREEN") return "approved";
  if (k?.answer === "RED") return "rejected";
  if (k?.status === "pending_review") return "pending";
  return "none";
}

function showAlert(message: string) {
  const wa = tg();
  if (wa?.showAlert) wa.showAlert(message);
  else window.alert(message);
}

export function Flow({ resetLabel, initialScreen }: Props) {
  const [step, setStep] = useState<Step>("welcome");
  const [booted, setBooted] = useState(false);
  const [profile, setProfile] = useState<ProfileT | null>(() => getProfile());
  const [orders, setOrders] = useState<ApiOrder[]>([]);
  const [kycState, setKycState] = useState<KycState>("none");
  const [draft, setDraft] = useState<Draft>({});
  // куда уйти после ввода email/адреса: в онбординге → дальше по цепочке, из кабинета → в профиль
  const [emailNext, setEmailNext] = useState<"kyc" | "profile" | "sherp">("kyc");
  const [addressNext, setAddressNext] = useState<"kyc" | "profile" | "sherp">("kyc");
  // ось sherp_joinder: после оферты брокера (или сразу после GREEN) — подписание Договора присоединения ШЭРП
  const [sherpEnabled, setSherpEnabled] = useState(false);
  const [sherpSigned, setSherpSigned] = useState(false);
  const [viewingOrderId, setViewingOrderId] = useState<string | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [usdtAsset, setUsdtAsset] = useState<UsdtAsset | null>(null);
  const [rate, setRate] = useState<number | null>(null);
  const [withdrawEnabled, setWithdrawEnabled] = useState(false);
  // видимость кнопок главного экрана — из промокода клиента. Стартуем со «всё скрыто» и
  // показываем только разрешённое после ответа /v1/account: иначе кнопки мигают и прячутся.
  const [uiFlags, setUiFlags] = useState<UiFlags>({ show_support: false, show_referrals: false, show_cards: false });
  // не удалось загрузить счёт при первом входе в кабинет → показываем экран «нет связи» с повтором
  const [accountError, setAccountError] = useState(false);
  const [withdrawals, setWithdrawals] = useState<ApiWithdrawal[]>([]);
  const [viewWithdrawal, setViewWithdrawal] = useState<ApiWithdrawal | null>(null);
  const [usdtWd, setUsdtWd] = useState<CustodialWithdrawal | null>(null);
  const reloadAccount = useCallback(async () => {
    try {
      const r = await accountApi.get();
      setAccount(r.account);
      setUsdtAsset(r.assets?.usdt ?? null);
      setRate(r.rate);
      setWithdrawEnabled(r.withdraw_enabled);
      if (r.ui) setUiFlags(r.ui);
      setAccountError(false);
    } catch {
      // при первом входе (счёт ещё не загружен) это фатально — покажем экран «нет связи».
      // фоновое обновление уже загруженного кабинета не роняем: там останутся прежние данные.
      setAccountError(true);
    }
  }, []);
  // выпущенная карта и параметры её обменов (курсы/комиссии/лимиты) — грузим вместе со счётом
  const [card, setCard] = useState<IssuedCard | null>(null);
  const [cardConfig, setCardConfig] = useState<CardConfig | null>(null);
  // способ внешнего вывода с карты, выбранный на экране «Вывод с карты»
  const [cardWdMethod, setCardWdMethod] = useState<WithdrawalMethod>("swift");
  const reloadCard = useCallback(async () => {
    try {
      const r = await cardsApi.my();
      setCard(r.card);
      setCardConfig(r.config);
    } catch {
      /* карта опциональна — без неё кабинет работает как раньше */
    }
  }, []);
  const reloadWithdrawals = useCallback(async () => {
    try {
      const r = await withdrawalsApi.list();
      setWithdrawals(r.withdrawals);
    } catch {
      /* вывод опционален */
    }
  }, []);
  // при каждом заходе в кабинет обновляем счёт (баланс мог измениться) и заявки на вывод
  useEffect(() => {
    if (step === "profile") {
      void reloadAccount();
      void reloadWithdrawals();
      void reloadCard();
    }
  }, [step, reloadAccount, reloadWithdrawals, reloadCard]);
  const [waitCall, setWaitCall] = useState<{ req: WaitCallRequest; phone: string } | null>(null);

  useEffect(() => {
    initTelegram();
  }, []);

  const reloadOrders = useCallback(async () => {
    try {
      const r = await ordersApi.list();
      setOrders(r.orders);
    } catch {
      /* ignore — offline / not authorized yet */
    }
  }, []);

  const refreshKyc = useCallback(async () => {
    try {
      setKycState(kycStateFrom(await kycApi.status()));
    } catch {
      /* ignore */
    }
  }, []);

  // Источник истины — сервер. В Telegram опознаёмся по initData, в вебе — по сессии.
  // Если номер уже привязан (me.phone) — сразу в кабинет, без повторного входа.
  useEffect(() => {
    (async () => {
      try {
        const me = await authApi.me();
        if (me.phone) {
          const prev = getProfile();
          const kyc = await kycApi.status().catch(() => null);
          setKycState(kycStateFrom(kyc));
          const sherpOn = kyc?.process_config?.sherp_joinder === "sherp";
          setSherpEnabled(sherpOn);
          if (sherpOn) {
            const ss = await sherpApi.status().catch(() => null);
            setSherpSigned(!!ss?.signed);
          }
          const p: ProfileT = {
            phone: me.phone,
            // email/адрес — серверная истина: не наследуем из локального кэша (иначе
            // новый юзер на том же устройстве «унаследует» чужие email/адрес и проскочит гейт)
            email: me.email ?? undefined,
            address: me.address ?? undefined,
            display_name:
              tgUserName() ??
              prev?.display_name ??
              ([me.first_name, me.last_name].filter(Boolean).join(" ") || undefined),
            last_wallet: prev?.last_wallet,
            kyc:
              kyc?.answer === "GREEN"
                ? { applicant_id: kyc.applicant_id ?? null, verified_at: Date.now(), level: "id-and-liveness" }
                : undefined,
            created_at: prev?.created_at ?? Date.now(),
          };
          saveProfile(p);
          setProfile(p);
          void reloadOrders();
          // Реферальный код с лендинга (?ref=): пользователь опознан — привязываем его на
          // сервере. Ошибку глушим: код мог быть неверным или привязка уже есть.
          const ref = takeRefCode();
          if (ref) await partnerApi.attach(ref).catch(() => undefined);
          if (initialScreen === "support") {
            setStep("support");
          } else if (!p.email) {
            // email обязателен для подписания оферты у брокера — добираем, если его нет
            setEmailNext("profile");
            setStep("email");
          } else if (!p.address) {
            // структурированный адрес нужен брокеру для онбординга
            setAddressNext("profile");
            setStep("address");
          } else {
            // Открытие приложения не должно зависеть от брокера: всегда в кабинет.
            // Неподписанная оферта показывается баннером в профиле (по тапу → подписание).
            setStep("profile");
          }
        } else {
          // Диплинк в поддержку доступен и без подтверждённого телефона: в Telegram
          // авторизация идёт по initData, а в чат поддержки часто пишут как раз те,
          // кто застрял на верификации. Вне Telegram (нет initData) — обычный вход.
          const tgAuthed = !!tg()?.initData;
          setStep(initialScreen === "support" && tgAuthed ? "support" : "welcome");
        }
      } catch {
        const tgAuthed = !!tg()?.initData;
        setStep(initialScreen === "support" && tgAuthed ? "support" : "welcome");
      } finally {
        setBooted(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goProfile = useCallback(() => {
    void reloadOrders();
    void refreshKyc();
    setViewingOrderId(null);
    setViewWithdrawal(null);
    setDraft({});
    setStep("profile");
  }, [reloadOrders, refreshKyc]);

  const startBuy = useCallback(() => {
    // кошелёк больше не нужен — пополнение идёт на виртуальную карту; сразу к сумме
    setStep("order");
  }, []);

  // Пополнение USDT — депозит крипты на свой кастодиальный кошелёк (без KYC): экран с QR/адресом
  const onTopUpUsdt = useCallback(() => setStep("topup_usdt"), []);

  // Вход на KYC: email и адрес обязательны для онбординга к брокеру и должны быть
  // собраны ДО верификации. Иначе клиент проходит iDenfy, а онбординг падает на
  // «missing email/address». Поэтому при нехватке — сперва ведём на email/адрес.
  const goKyc = useCallback(() => {
    if (!profile?.email) {
      setEmailNext("kyc");
      setStep("email");
    } else if (!profile?.address) {
      setAddressNext("kyc");
      setStep("address");
    } else {
      setStep("kyc");
    }
  }, [profile]);

  // Вход в подписание ШЭРП: для email-OTP подписи нужен email. Если его нет
  // (клиент дошёл до этого шага в обход сбора контактов) — сперва ведём на email.
  const goSherp = useCallback(() => {
    if (!profile?.email) {
      setEmailNext("sherp");
      setStep("email");
    } else {
      setStep("sherp");
    }
  }, [profile]);

  // Пополнение (RUB) — без верификации: KYC и подписание требуются только на выводе средств.
  const onBuy = useCallback(() => {
    startBuy();
  }, [startBuy]);

  // тап по значку верификации в профиле → хаб из двух ступеней (KYC + ИПДС)
  const onVerify = useCallback(() => setStep("verify"), []);

  const openOrder = useCallback((id: string) => {
    setViewingOrderId(id);
    setStep("status");
  }, []);

  const openWithdrawal = useCallback((w: ApiWithdrawal) => {
    setViewWithdrawal(w);
    setStep("withdraw_status");
  }, []);

  // Вход в вывод (RUB-счёт): доступен всем при положительном балансе. Способы карта/SWIFT — без
  // чита, TRC20-кошелёк — только с читом (фильтруется в Withdraw). Верификация (KYC + подписание
  // ШЭРП) требуется именно ЗДЕСЬ, а не при пополнении.
  const onWithdraw = useCallback(() => {
    if (!account || account.balance_rub <= 0) {
      showAlert("На счёте нет средств для вывода. Сначала пополните счёт.");
      return;
    }
    if (kycState === "approved") {
      // нельзя выводить без подписанного Заявления о присоединении ШЭРП (если ось включена)
      if (sherpEnabled && !sherpSigned) {
        showAlert("Сначала подпишите Заявление о присоединении к платёжному оператору ЗАО «ШЭРП».");
        goSherp();
        return;
      }
      setStep("withdraw");
    } else if (kycState === "pending") {
      showAlert("Документы на проверке. Мы подтвердим верификацию в ближайшее время — после этого можно будет вывести средства.");
    } else {
      goKyc();
    }
  }, [account, kycState, sherpEnabled, sherpSigned, goKyc, goSherp]);

  // Вывод USDT с кастодиального кошелька — без KYC (депозит/распоряжение своей криптой).
  const onWithdrawUsdt = useCallback(() => {
    if (!withdrawEnabled) return;
    setStep("usdt_withdraw");
  }, [withdrawEnabled]);

  const reset = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    clearAll();
    setProfile(null);
    setOrders([]);
    setKycState("none");
    setDraft({});
    setWaitCall(null);
    setViewingOrderId(null);
    setStep("welcome");
  }, []);

  // Единый переход после подтверждения телефона (звонок ИЛИ Telegram-кнопка):
  // тянем email/адрес с сервера и ведём по онбордингу брокера.
  const finishPhone = useCallback(
    async (phone: string) => {
      const base = profile ?? getProfile();
      let me: Awaited<ReturnType<typeof authApi.me>> | null = null;
      try {
        me = await authApi.me();
      } catch {
        /* ignore — ниже соберём недостающее */
      }
      const p: ProfileT = {
        phone,
        email: me?.email ?? undefined,
        address: me?.address ?? undefined,
        display_name: base?.display_name ?? tgUserName() ?? undefined,
        last_wallet: base?.last_wallet,
        kyc: base?.kyc,
        created_at: base?.created_at ?? Date.now(),
      };
      saveProfile(p);
      setProfile(p);
      setWaitCall(null);
      // Онбординг больше НЕ ведёт на верификацию: собираем только email + адрес и уходим
      // в кабинет. KYC проходится позже из профиля (тап по бейджу «Верификация не пройдена»).
      if (!p.email) {
        setEmailNext("profile");
        setStep("email");
      } else if (!p.address) {
        setAddressNext("profile");
        setStep("address");
      } else {
        setStep("profile");
      }
    },
    [profile],
  );

  if (!booted) return null;

  switch (step) {
    case "welcome":
      return <Welcome onNext={() => setStep("phone")} />;

    case "phone":
      return (
        <PhoneWeb
          onBack={() => setStep("welcome")}
          onNext={(req, phone) => {
            setWaitCall({ req, phone });
            setDraft((d) => ({ ...d, phone }));
            setStep("waitcall");
          }}
          onVerified={(phone) => {
            setDraft((d) => ({ ...d, phone }));
            void finishPhone(phone);
          }}
        />
      );

    case "waitcall":
      if (!waitCall) {
        setStep("phone");
        return null;
      }
      return (
        <WaitCall
          req={waitCall.req}
          phone={waitCall.phone}
          onBack={() => {
            setWaitCall(null);
            setStep("phone");
          }}
          onConfirmed={() => finishPhone(waitCall.phone)}
        />
      );

    case "email":
      return (
        <Email
          initial={profile?.email}
          onBack={goProfile}
          onNext={(email) => {
            updateProfile({ email });
            setProfile((p) => (p ? { ...p, email } : p));
            // для ШЭРП нужен только email — после него сразу на подписание
            if (emailNext === "sherp") {
              setStep("sherp");
            } else if (!profile?.address) {
              // если адреса тоже нет — сразу ведём на него, в тот же пункт назначения,
              // куда шёл email-шаг (профиль при открытии приложения, KYC при онбординге)
              setAddressNext(emailNext);
              setStep("address");
            } else if (emailNext === "profile") {
              goProfile();
            } else {
              setStep("kyc");
            }
          }}
        />
      );

    case "address":
      return (
        <Address
          initial={profile?.address}
          onBack={goProfile}
          onNext={(address) => {
            updateProfile({ address });
            setProfile((p) => (p ? { ...p, address } : p));
            if (addressNext === "kyc") setStep("kyc");
            else goProfile();
          }}
        />
      );

    case "kyc":
      return (
        <Kyc
          onBack={() => goProfile()}
          onNext={(status) => {
            const approved = status.answer === "GREEN";
            setKycState(approved ? "approved" : "pending");
            const base = profile ?? getProfile() ?? { phone: draft.phone ?? "", created_at: Date.now() };
            const p: ProfileT = {
              ...base,
              phone: base.phone || draft.phone || "",
              display_name: base.display_name ?? tgUserName() ?? undefined,
              kyc: approved
                ? { applicant_id: status.applicant_id ?? null, verified_at: Date.now(), level: "id-and-liveness" }
                : base.kyc,
            };
            saveProfile(p);
            setProfile(p);
            // после GREEN — оферта брокера (doc_signing=broker), затем Договор присоединения ШЭРП
            // (sherp_joinder=sherp). Если брокерской оферты нет — сразу к ШЭРП, иначе в профиль.
            const signing = status.process_config?.doc_signing === "broker";
            const sherpOn = status.process_config?.sherp_joinder === "sherp";
            setSherpEnabled(sherpOn);
            if (approved && signing) setStep("agreement");
            else if (approved && sherpOn) goSherp();
            else setStep("profile");
          }}
        />
      );

    case "agreement":
      return <Agreement onDone={() => (sherpEnabled ? goSherp() : goProfile())} />;

    case "sherp":
      return (
        <Sherp
          onDone={async () => {
            // статус мог не измениться (выход без подписи) — берём правду с сервера
            const ss = await sherpApi.status().catch(() => null);
            setSherpSigned(!!ss?.signed);
            goProfile();
          }}
        />
      );

    case "profile":
      if (!profile) {
        setStep("welcome");
        return null;
      }
      // до первого успешного ответа /v1/account кабинет не рисуем: либо экран «нет связи»
      // с повтором (запрос упал), либо короткий спиннер (ещё грузим) — чтобы не мигать нулями
      if (!account) {
        if (accountError) return <ConnectionError onRetry={reloadAccount} />;
        return (
          <div className="grid min-h-dvh place-items-center bg-surface-base">
            <Spinner className="h-8 w-8" />
          </div>
        );
      }
      return (
        <Profile
          profile={profile}
          orders={orders}
          kycState={kycState}
          account={account}
          usdt={usdtAsset}
          rate={rate}
          withdrawEnabled={withdrawEnabled}
          withdrawals={withdrawals}
          onBuy={onBuy}
          onTopUpUsdt={onTopUpUsdt}
          onWithdraw={onWithdraw}
          onWithdrawUsdt={onWithdrawUsdt}
          onCards={uiFlags.show_cards ? () => setStep("cards") : undefined}
          card={uiFlags.show_cards ? card : null}
          onOpenCard={() => setStep("card")}
          onCardTopUp={() => setStep("card_topup")}
          onCardWithdraw={() => setStep("card_withdraw")}
          showSupport={uiFlags.show_support}
          onOpenWithdrawal={openWithdrawal}
          onVerify={onVerify}
          onOpenOrder={openOrder}
          onHideOrder={async (id) => {
            setOrders((list) => list.map((o) => (o.id === id ? { ...o, hidden: true } : o)));
            try {
              await ordersApi.hide(id);
            } catch {
              void reloadOrders();
            }
          }}
          onUnhideOrder={async (id) => {
            setOrders((list) => list.map((o) => (o.id === id ? { ...o, hidden: false } : o)));
            try {
              await ordersApi.unhide(id);
            } catch {
              void reloadOrders();
            }
          }}
          onSupport={() => setStep("support")}
          onAgreement={() => setStep("agreement")}
          onSherp={goSherp}
          onReset={reset}
          resetLabel={resetLabel}
        />
      );

    case "cards":
      return (
        <Cards
          onBack={goProfile}
          onIssued={(issued) => {
            setCard(issued);
            void reloadCard();
            void reloadAccount();  // за выпуск могли списать рубли
            setStep("card");
          }}
        />
      );

    // ── Выпущенная карта: экран карты и её операции ───────────────────────
    case "card": {
      if (!card) return <Cards onBack={goProfile} onIssued={(c) => { setCard(c); setStep("card"); }} />;
      return (
        <CardScreen
          card={card}
          onTopUp={() => setStep("card_topup")}
          onWithdraw={() => setStep("card_withdraw")}
          onBack={goProfile}
        />
      );
    }

    case "card_topup": {
      if (!card || !cardConfig || !account) return null;
      return (
        <CardTopUp
          card={card}
          accountRub={account.balance_rub}
          config={cardConfig}
          onDone={() => {
            void reloadCard();
            void reloadAccount();
            setStep("card");
          }}
          onBack={() => setStep("card")}
        />
      );
    }

    case "card_refund": {
      if (!card || !cardConfig || !account) return null;
      return (
        <CardRefund
          card={card}
          accountRub={account.balance_rub}
          config={cardConfig}
          onDone={() => {
            void reloadCard();
            void reloadAccount();
            setStep("card");
          }}
          onBack={() => setStep("card_withdraw")}
        />
      );
    }

    case "card_crypto": {
      if (!card || !cardConfig) return null;
      return (
        <CardCrypto
          card={card}
          config={cardConfig}
          onDone={() => {
            void reloadCard();
            setStep("card");
          }}
          onBack={() => setStep("card_withdraw")}
        />
      );
    }

    case "card_withdraw": {
      if (!card) return null;
      return (
        <CardWithdraw
          card={card}
          onBack={() => setStep("card")}
          onPick={(target: CardWithdrawTarget) => {
            if (target === "account") return setStep("card_refund");
            if (target === "crypto") return setStep("card_crypto");
            setCardWdMethod(target === "swift" ? "swift" : "card");
            setStep("card_withdraw_form");
          }}
        />
      );
    }

    // внешний вывод с карты — общий экран заявки, но сумма в долларах и списание с карты
    case "card_withdraw_form": {
      if (!card) return null;
      return (
        <Withdraw
          balance={account?.balance_rub ?? 0}
          source="card"
          balanceUsd={card.balance_usd}
          initialMethod={cardWdMethod}
          onNext={(w) => {
            void reloadCard();
            void reloadWithdrawals();
            setViewWithdrawal(w);
            setStep("withdraw_status");
          }}
          onBack={() => setStep("card_withdraw")}
          onOpenSof={() => setStep("sof")}
        />
      );
    }

    case "topup_usdt":
      return (
        <TopUpUsdt address={usdtAsset?.address ?? ""} onBack={goProfile} />
      );

    case "verify":
      return (
        <VerifyHub
          kycState={kycState}
          onKyc={goKyc}
          onSof={() => setStep("sof")}
          onBack={goProfile}
        />
      );

    case "sof":
      return <Sof onBack={() => setStep("verify")} />;

    case "support":
      return <Support onBack={goProfile} />;

    case "wallet":
      return (
        <Wallet
          initial={draft.wallet ?? profile?.last_wallet}
          onBack={goProfile}
          onNext={(wallet) => {
            setDraft((d) => ({ ...d, wallet }));
            if (profile) {
              updateProfile({ last_wallet: wallet });
              setProfile((p) => (p ? { ...p, last_wallet: wallet } : p));
            }
            setStep("order");
          }}
        />
      );

    case "order":
      return (
        <Order
          onBack={() => setStep("profile")}
          onNext={(order) => {
            setDraft((d) => ({ ...d, order }));
            setOrders((list) => [order, ...list]);
            setStep("status");
          }}
        />
      );

    case "status": {
      const id = viewingOrderId ?? draft.order?.id;
      const order = orders.find((o) => o.id === id) ?? draft.order ?? null;
      if (!order) {
        goProfile();
        return null;
      }
      const live = order.status !== "completed" && order.status !== "rejected";
      return <Status order={order} live={live} onDone={goProfile} kyc={kycState} onVerify={onVerify} />;
    }

    case "withdraw":
      return (
        <Withdraw
          balance={account?.balance_rub ?? 0}
          onBack={goProfile}
          onOpenSof={() => setStep("sof")}
          onNext={(w) => {
            setWithdrawals((list) => [w, ...list]);
            setViewWithdrawal(w);
            void reloadAccount();
            setStep("withdraw_status");
          }}
        />
      );

    case "withdraw_status": {
      const w = viewWithdrawal;
      if (!w) {
        goProfile();
        return null;
      }
      const live = w.status === "pending" || w.status === "processing";
      return <WithdrawStatus withdrawal={w} live={live} onDone={goProfile} />;
    }

    case "usdt_withdraw":
      return (
        <UsdtWithdraw
          onBack={goProfile}
          onNext={(w) => {
            setUsdtWd(w);
            setStep("usdt_withdraw_status");
          }}
        />
      );

    case "usdt_withdraw_status": {
      if (!usdtWd) {
        goProfile();
        return null;
      }
      return (
        <UsdtWithdrawStatus
          withdrawal={usdtWd}
          onDone={() => {
            void reloadAccount();
            goProfile();
          }}
        />
      );
    }
  }
}
